import type { IconLibraryCategory, IconLibraryItem, IconLibraryPayload } from '@/lib/icon-library';
import { apiDelete, apiGet, apiPost, apiPut } from './api-client';

export type IconLibraryListParams = {
  q?: string;
  category?: string;
  sort?: string;
};

export async function fetchAdminIcons(params: IconLibraryListParams = {}): Promise<IconLibraryItem[]> {
  const search = new URLSearchParams();
  if (params.q?.trim()) search.set('q', params.q.trim());
  if (params.category && params.category !== 'all') search.set('category', params.category);
  if (params.sort) search.set('sort', params.sort);
  const qs = search.toString();
  const res = await apiGet<{ data: IconLibraryItem[] }>(
    `/admin/icons${qs ? `?${qs}` : ''}`,
    true
  );
  return res.data ?? [];
}

export async function fetchAdminIcon(id: number): Promise<IconLibraryItem> {
  const res = await apiGet<{ data: IconLibraryItem }>(`/admin/icons/${id}`, true);
  return res.data;
}

export async function fetchAdminIconCategories(): Promise<IconLibraryCategory[]> {
  const res = await apiGet<{ data: IconLibraryCategory[] }>('/admin/icons/categories', true);
  return res.data ?? [];
}

export async function createAdminIcon(
  payload: IconLibraryPayload
): Promise<{ icon: IconLibraryItem; warnings: string[]; hadDangerous: boolean }> {
  const res = await apiPost<{
    data: IconLibraryItem;
    meta?: { warnings?: string[]; hadDangerous?: boolean };
  }>('/admin/icons', payload, true);
  return {
    icon: res.data,
    warnings: res.meta?.warnings ?? [],
    hadDangerous: Boolean(res.meta?.hadDangerous),
  };
}

export async function updateAdminIcon(
  id: number,
  payload: Partial<IconLibraryPayload>
): Promise<{ icon: IconLibraryItem; warnings: string[]; hadDangerous: boolean }> {
  const res = await apiPut<{
    data: IconLibraryItem;
    meta?: { warnings?: string[]; hadDangerous?: boolean };
  }>(`/admin/icons/${id}`, payload, true);
  return {
    icon: res.data,
    warnings: res.meta?.warnings ?? [],
    hadDangerous: Boolean(res.meta?.hadDangerous),
  };
}

export async function deleteAdminIcon(id: number): Promise<void> {
  await apiDelete(`/admin/icons/${id}`, true);
}

export async function uploadAdminIcon(
  file: File,
  meta: Partial<Omit<IconLibraryPayload, 'svgContent'>> = {}
): Promise<{ icon: IconLibraryItem; warnings: string[]; hadDangerous: boolean }> {
  const form = new FormData();
  form.append('file', file);
  if (meta.name) form.append('name', meta.name);
  if (meta.slug) form.append('slug', meta.slug);
  if (meta.category) form.append('category', meta.category);
  if (meta.description) form.append('description', meta.description);
  if (meta.renderMode) form.append('renderMode', meta.renderMode);
  if (meta.tags != null) {
    form.append('tags', typeof meta.tags === 'string' ? meta.tags : meta.tags.join(','));
  }
  const res = await apiPost<{
    data: IconLibraryItem;
    meta?: { warnings?: string[]; hadDangerous?: boolean };
  }>('/admin/icons/upload', form, true);
  return {
    icon: res.data,
    warnings: res.meta?.warnings ?? [],
    hadDangerous: Boolean(res.meta?.hadDangerous),
  };
}

export async function importPublicIcons(): Promise<number> {
  const res = await apiPost<{ data: { imported: number } }>('/admin/icons/import-public', {}, true);
  return res.data?.imported ?? 0;
}

/** Catálogo público (leve, sem svgContent). */
export async function fetchPublicIcons(params: IconLibraryListParams = {}): Promise<IconLibraryItem[]> {
  const search = new URLSearchParams();
  if (params.q?.trim()) search.set('q', params.q.trim());
  if (params.category && params.category !== 'all') search.set('category', params.category);
  const qs = search.toString();
  const res = await apiGet<{ data: IconLibraryItem[] }>(`/icons${qs ? `?${qs}` : ''}`);
  return res.data ?? [];
}

export async function fetchPublicIcon(slug: string): Promise<IconLibraryItem | null> {
  try {
    const res = await apiGet<{ data: IconLibraryItem }>(`/icons/${encodeURIComponent(slug)}`);
    return res.data ?? null;
  } catch {
    return null;
  }
}
